/**
 * @velyn-custom BEGIN — vely-recently-viewed.js
 * Same localStorage fetch as Core recently-viewed-products + Swiper with Vely pill pagination.
 * Desktop carousel when product count > items_per_row (default 4).
 * Mobile always carousels when more than one product (Figma peek).
 * Track offsets: slidesOffsetBefore/After + last-snap pin (STL/FC pattern).
 */
import { LazyLoader } from "@NextSkyTheme/lazy-load";

const MOBILE_MQ = "(max-width: 767.98px)";
const getSwiper = () => window.Swiper;

class VelyRecentlyViewed extends HTMLElement {
  connectedCallback() {
    if (this._velyRvObserving) return;
    this._velyRvObserving = true;

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      this.initData();
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 100px 0px",
    }).observe(this);
  }

  disconnectedCallback() {
    this.destroySlider();
    this._velyRvObserving = false;
    this._velyRvInitStarted = false;
  }

  initData() {
    let savedProductsArr = null;
    try {
      savedProductsArr = JSON.parse(
        localStorage.getItem("recently-viewed-products")
      );
    } catch (error) {
      savedProductsArr = null;
    }
    this.getStoredProducts(savedProductsArr);
  }

  getStoredProducts(arr) {
    const limit = this.dataset?.limit;
    let productAjaxURL = "";

    if (limit && arr && arr.length > 0) {
      const idsToUse = arr.slice().slice(0, Number(limit) || arr.length);
      const query = idsToUse.join("%20OR%20id:");
      productAjaxURL = `&q=id:${query}`;
    }

    fetch(`${this.dataset.url}${productAjaxURL}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recentlyViewed = html.querySelector("vely-recently-viewed");
        if (recentlyViewed && recentlyViewed.innerHTML.trim().length) {
          this.destroySlider();
          this.innerHTML = recentlyViewed.innerHTML;

          // Cart/search first paint has empty results — sync carousel flags/classes from fetched markup.
          [
            "carouselDesktop",
            "carouselMobile",
            "desktop",
            "tablet",
            "mobile",
            "spacing",
            "spacingMobile",
          ].forEach((key) => {
            if (recentlyViewed.dataset[key] != null) {
              this.dataset[key] = recentlyViewed.dataset[key];
            }
          });

          this.classList.toggle(
            "vely-rv--grid-desktop",
            recentlyViewed.classList.contains("vely-rv--grid-desktop")
          );
          this.classList.toggle(
            "vely-rv--grid-mobile",
            recentlyViewed.classList.contains("vely-rv--grid-mobile")
          );

          const section = this.closest(".section-vely-recently-viewed");
          const fetchedSection = html.querySelector(
            ".section-vely-recently-viewed"
          );
          if (section && fetchedSection) {
            section.classList.toggle(
              "vely-rv--has-carousel",
              fetchedSection.classList.contains("vely-rv--has-carousel")
            );
          }

          new LazyLoader(".image-lazy-load");
          this.waitForSwiper(() => this.initSlider());
        }
      })
      .catch((error) => {
        console.error("[vely-recently-viewed]", error);
      });
  }

  waitForSwiper(callback, attempt = 0) {
    const hasSwiper = typeof getSwiper() === "function";
    const hasPagination = Boolean(window.VelySwiperPagination?.refresh);
    if (hasSwiper && hasPagination) {
      callback();
      return;
    }
    if (attempt > 80) {
      console.warn(
        "[vely-recently-viewed] Swiper or VelySwiperPagination not available"
      );
      return;
    }
    setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
  }

  isCarouselActiveForViewport() {
    const isMobile = window.matchMedia(MOBILE_MQ).matches;
    if (isMobile) {
      return this.dataset.carouselMobile === "true";
    }
    return this.dataset.carouselDesktop === "true";
  }

  destroySlider() {
    if (this._swiper) {
      this._swiper.destroy(true, true);
      this._swiper = null;
    }
    this._paginationEl = null;
    this.classList.remove("vely-rv--ready");
  }

  getLeftInsetPx() {
    const section = this.closest(".section-vely-recently-viewed");
    const shell = this.querySelector(".vely-rv__slider-shell");
    if (!section || !shell) return 0;

    const titleBox = section.querySelector(
      ".vely-rv__header-wrap, .vely-rv__section-inner > .container, .vely-rv__section-inner > .full-width"
    );
    const shellLeft = shell.getBoundingClientRect().left;

    if (titleBox) {
      const pad =
        parseFloat(getComputedStyle(titleBox).paddingInlineStart) || 0;
      return Math.max(
        0,
        Math.round(titleBox.getBoundingClientRect().left + pad - shellLeft)
      );
    }

    return 0;
  }

  getRightInsetPx() {
    const section = this.closest(".section-vely-recently-viewed");
    const shell = this.querySelector(".vely-rv__slider-shell");
    if (!section || !shell) return 0;

    const titleBox = section.querySelector(
      ".vely-rv__header-wrap, .vely-rv__section-inner > .container, .vely-rv__section-inner > .full-width"
    );
    const shellRight = shell.getBoundingClientRect().right;

    if (titleBox) {
      const pad =
        parseFloat(getComputedStyle(titleBox).paddingInlineEnd) || 0;
      return Math.max(
        0,
        Math.round(shellRight - (titleBox.getBoundingClientRect().right - pad))
      );
    }

    return this.getLeftInsetPx();
  }

  getSlidesPerViewNumber(swiper) {
    const spv = swiper?.params?.slidesPerView ?? 1;
    if (typeof spv === "number" && Number.isFinite(spv)) return spv;
    if (typeof spv === "string" && spv !== "auto") {
      const parsed = parseFloat(spv);
      if (Number.isFinite(parsed)) return parsed;
    }
    return 1;
  }

  /**
   * Keep only snaps that still fill the viewport (no empty trailing columns).
   * Then pin the last snap to maxTranslate so the last card meets container right.
   */
  clampSnapGridToFilledEnd(swiper) {
    if (!swiper?.snapGrid?.length || !swiper.slides?.length) return;

    const spv = this.getSlidesPerViewNumber(swiper);
    const slideCount = swiper.slides.length;
    const maxStart = Math.max(0, Math.ceil(slideCount - spv - 1e-6));
    const maxPages = maxStart + 1;

    let snaps = swiper.snapGrid.slice(
      0,
      Math.min(maxPages, swiper.snapGrid.length)
    );
    const maxSnap = -swiper.maxTranslate();

    if (!Number.isFinite(maxSnap)) return;

    if (snaps.length === 0) {
      snaps = [maxSnap];
    } else {
      snaps[snaps.length - 1] = maxSnap;
    }

    swiper.snapGrid = snaps;

    const current = swiper.getTranslate?.() ?? 0;
    if (current < maxSnap) {
      swiper.setTranslate?.(maxSnap);
      swiper.updateProgress?.();
      swiper.updateSlidesClasses?.();
    }
  }

  applyTrackOffsets(swiper = this._swiper) {
    if (!swiper) return;
    const carouselActive = this.isCarouselActiveForViewport();
    const before = carouselActive ? this.getLeftInsetPx() : 0;
    const after = carouselActive ? this.getRightInsetPx() : 0;

    const beforeChanged = swiper.params.slidesOffsetBefore !== before;
    const afterChanged = swiper.params.slidesOffsetAfter !== after;

    swiper.params.slidesOffsetBefore = before;
    swiper.params.slidesOffsetAfter = after;

    const bps = swiper.params.breakpoints;
    if (bps && typeof bps === "object") {
      Object.keys(bps).forEach((key) => {
        if (!bps[key]) return;
        bps[key].slidesOffsetBefore = before;
        bps[key].slidesOffsetAfter = after;
      });
    }

    if (beforeChanged || afterChanged) {
      swiper.update();
    } else if (carouselActive) {
      swiper.updateSize?.();
      swiper.updateSlides?.();
      swiper.updateProgress?.();
    }

    if (carouselActive) {
      this.clampSnapGridToFilledEnd(swiper);
    }
  }

  refreshPagination() {
    const pagination = window.VelySwiperPagination;
    if (!pagination || !this._swiper || !this._paginationEl) return;
    // Do not force bulletCount === slideCount — multi-card carousels are page-based.
    pagination.refresh(this._swiper, this._paginationEl);
  }

  syncControls(swiper) {
    const controls = this.querySelector("[data-vely-pagination-anchor]");
    if (!controls || !swiper) return;

    this.refreshPagination();

    const carouselActive = this.isCarouselActiveForViewport();
    const shouldShow =
      carouselActive &&
      swiper.enabled !== false &&
      (window.VelySwiperPagination?.shouldShowPagination?.(swiper) ?? false);
    controls.hidden = !shouldShow;
  }

  syncPagination(swiper = this._swiper) {
    if (!this.isCarouselActiveForViewport() || !this._paginationEl || !swiper) {
      return;
    }
    window.VelySwiperPagination?.setActiveBullet?.(swiper, this._paginationEl);
  }

  initSlider() {
    const SwiperCtor = getSwiper();
    if (typeof SwiperCtor !== "function") return;

    const swiperEl = this.querySelector(".vely-rv__swiper");
    if (!swiperEl) return;

    this.destroySlider();
    this._paginationEl = this.querySelector(".vely-swiper-pagination-pill");

    const itemDesktop = Number(this.dataset.desktop) || 4;
    const itemTablet = Number(this.dataset.tablet) || Math.max(1, itemDesktop - 1);
    const itemMobile = Number(this.dataset.mobile) || 1.15;
    const gapDesktop = Number(this.dataset.spacing) || 24;
    const gapMobile = Number(this.dataset.spacingMobile) || 16;
    const carouselDesktop = this.dataset.carouselDesktop === "true";
    const carouselMobile = this.dataset.carouselMobile === "true";
    const leftInset = this.isCarouselActiveForViewport()
      ? this.getLeftInsetPx()
      : 0;
    const rightInset = this.isCarouselActiveForViewport()
      ? this.getRightInsetPx()
      : 0;

    try {
      this._swiper = new SwiperCtor(swiperEl, {
        slidesPerView: itemMobile,
        spaceBetween: gapMobile,
        speed: 300,
        loop: false,
        grabCursor: true,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        enabled: carouselMobile,
        slidesOffsetBefore: leftInset,
        slidesOffsetAfter: rightInset,
        breakpoints: {
          768: {
            slidesPerView: itemTablet,
            spaceBetween: gapDesktop,
            enabled: carouselDesktop,
            slidesOffsetBefore: leftInset,
            slidesOffsetAfter: rightInset,
          },
          1025: {
            slidesPerView: itemDesktop,
            spaceBetween: gapDesktop,
            enabled: carouselDesktop,
            slidesOffsetBefore: leftInset,
            slidesOffsetAfter: rightInset,
          },
        },
        on: {
          init: (swiper) => {
            window.VelySwiperPagination?.pinInFlow?.(this._paginationEl);
            this.applyTrackOffsets(swiper);
            requestAnimationFrame(() => this.syncControls(swiper));
          },
          resize: (swiper) => {
            this.applyTrackOffsets(swiper);
            swiper.updateSize?.();
            swiper.updateSlides?.();
            swiper.updateProgress?.();
            swiper.updateSlidesClasses?.();
            this.syncControls(swiper);
          },
          breakpoint: (swiper) => {
            this.applyTrackOffsets(swiper);
            this.syncControls(swiper);
          },
          slidesLengthChange: (swiper) => {
            this.syncControls(swiper);
          },
          slideChange: (swiper) => {
            this.syncPagination(swiper);
          },
          slideChangeTransitionEnd: (swiper) => {
            this.syncPagination(swiper);
          },
          progress: (swiper) => {
            this.syncPagination(swiper);
          },
          reachEnd: (swiper) => {
            this.syncPagination(swiper);
          },
        },
      });
    } catch (error) {
      this._swiper = null;
      console.warn("[vely-recently-viewed] Swiper init failed", error);
      return;
    }

    window.VelySwiperPagination?.pinInFlow?.(this._paginationEl);
    requestAnimationFrame(() => {
      this.applyTrackOffsets(this._swiper);
      this.syncControls(this._swiper);
    });
    this.classList.add("vely-rv--ready");
  }
}

if (!customElements.get("vely-recently-viewed")) {
  customElements.define("vely-recently-viewed", VelyRecentlyViewed);
}

document.addEventListener("shopify:section:load", (event) => {
  const host = event.target?.querySelector?.("vely-recently-viewed");
  if (!host) return;
  host._velyRvObserving = false;
  host.disconnectedCallback?.();
  host.connectedCallback();
});
/* @velyn-custom END — vely-recently-viewed.js */
